/* eslint-disable @typescript-eslint/consistent-type-assertions */
import * as React from 'react'
import { Temporal } from '@js-temporal/polyfill'
import { clsx } from 'clsx'
import { Time } from '@internationalized/date'
import {
  useDateSegment,
  useTimeField,
  type TimeValue,
  type DateValue,
} from 'react-aria'
import type { DateFieldState, DateSegment } from 'react-stately'
import { useTimeFieldState } from 'react-stately'

import { useMergeRefs } from '../../hooks/useMergeRefs'
import { DropdownMenu, type DropdownMenuContentProps } from '../DropdownMenu'
import { ScrollArea } from '../ScrollArea'

import { Label } from '../Label'
import styles from './TimeInput.module.css'
import { UnstyledButton } from '../UnstyledButton'
import { Flex } from '../Flex'
import { Skeleton } from '../Skeleton'
import { PinIcon } from '../icons'
import { Tooltip } from '../Tooltip'

type HandleInteractOutsideEvent = NonNullable<
  DropdownMenuContentProps['onInteractOutside']
> extends (event: infer E) => void
  ? E
  : never

/**
 * Converts a Temporal.PlainTime object to a TimeValue object for React Aria
 *
 * @param plainTime - The Temporal.PlainTime object to convert
 * @returns A TimeValue object compatible with React Aria, or undefined if no input is provided
 */
function plainTimeToTimeValue(
  plainTime?: Temporal.PlainTime,
): Time | undefined {
  if (!plainTime) return undefined

  return new Time(plainTime.hour, plainTime.minute, plainTime.second)
}

/**
 * Converts a React Aria TimeValue object to a Temporal.PlainTime object
 *
 * @param timeValue - The TimeValue object to convert
 * @returns A Temporal.PlainTime object, or null if no input is provided
 */
function timeValueToPlainTime(
  timeValue?: TimeValue,
): Temporal.PlainTime | null {
  if (!timeValue) return null

  return new Temporal.PlainTime(
    timeValue.hour ?? 0,
    timeValue.minute ?? 0,
    timeValue.second ?? 0,
  )
}

function isEventOverlappingRef(
  e: HandleInteractOutsideEvent,
  ref: HTMLElement | null,
): boolean {
  if (!ref) return false
  const triggerRect = ref.getBoundingClientRect()

  // Only handle pointer events, not focus events
  if (e.type !== 'dismissableLayer.pointerDownOutside') {
    return false
  }

  const pointerEvent = e.detail.originalEvent as PointerEvent
  const pointerCoordinates = {
    x: pointerEvent.clientX,
    y: pointerEvent.clientY,
  }
  return (
    pointerCoordinates.x > triggerRect.x &&
    pointerCoordinates.y > triggerRect.y &&
    pointerCoordinates.x < triggerRect.x + triggerRect.width &&
    pointerCoordinates.y < triggerRect.y + triggerRect.height
  )
}

interface TimeSegmentProps {
  segment: DateSegment
  state: DateFieldState
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>
  focusRef?: React.MutableRefObject<HTMLDivElement | null>
}

const ConditionalTooltip: React.ElementType<
  {
    children: React.ReactNode
    showTooltip: boolean
    key: string
    className?: string
    align?: 'start' | 'center' | 'end'
  },
  'div'
> = ({ children, showTooltip, align = 'start', ...props }) => {
  if (!showTooltip) return <div {...props}>{children}</div>
  return (
    <Tooltip align={align} delay={0} content="Slot in use">
      {children}
    </Tooltip>
  )
}

/**
 * Individual time segment component (hour, minute, second, etc.)
 */
function TimeSegment({
  segment,
  state,
  onKeyDown,
  focusRef,
}: TimeSegmentProps): React.ReactElement {
  const ref = React.useRef<HTMLDivElement>(null)
  const { segmentProps } = useDateSegment(segment, state, ref)

  return (
    <div
      {...segmentProps}
      ref={ref}
      className={clsx(
        styles.segment,
        styles[segment.type],
        segment.isPlaceholder && styles.placeholder,
      )}
      style={{
        ...segmentProps.style,
      }}
      onKeyDown={(e) => {
        segmentProps.onKeyDown?.(e)
        if (onKeyDown) {
          onKeyDown(e)
        }
      }}
      onFocus={(e) => {
        segmentProps.onFocus?.(e)
        if (segment.type !== 'literal' && focusRef) {
          focusRef.current = ref.current
        }
      }}
      role={segment.type !== 'literal' ? 'spinbutton' : undefined}
    >
      {segment.text}
    </div>
  )
}

/**
 * Formats a time zone string by replacing underscores with spaces.
 * @param {string} timeZone - The time zone string to format.
 * @returns {string} The formatted time zone string.
 */
export const formatTimeZone = (timeZone: string): string => {
  return timeZone.replace(/_/g, ' ')
}

export interface TimeSuggestion {
  time: Temporal.PlainTime
  isDisabled?: boolean
  isCurrentPostDate?: boolean
}

export interface TimeSuggestionsProp {
  data: TimeSuggestion[]
  loading: boolean
  label: string
}

// Unified time option interface
export interface TimeOption extends TimeSuggestion {
  label: string
}

const toTimeLabel = (time: Temporal.PlainTime, hourCycle: 12 | 24): string => {
  if (hourCycle === 24) {
    return time.toLocaleString('en-US', {
      hourCycle: 'h23',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Custom 12-hour format with 00:00 AM for midnight
  const hour = time.hour
  const minute = time.minute

  let displayHour: string
  let period: string

  if (hour === 0) {
    // Midnight: 00:00 AM
    displayHour = '00'
    period = 'AM'
  } else if (hour === 12) {
    // Noon: 12:00 PM
    displayHour = '12'
    period = 'PM'
  } else if (hour < 12) {
    // Morning: 01:00 AM - 11:00 AM
    displayHour = hour.toString().padStart(2, '0')
    period = 'AM'
  } else {
    // Afternoon/Evening: 01:00 PM - 11:00 PM
    displayHour = (hour - 12).toString().padStart(2, '0')
    period = 'PM'
  }

  const displayMinute = minute.toString().padStart(2, '0')

  return `${displayHour}:${displayMinute} ${period}`
}

/**
 * Generates time options using PlainTime
 */
function generateTimeOptions(
  hourCycle: 12 | 24,
  granularity: 'hour' | 'minute' | 'second' = 'minute',
  min?: Temporal.PlainTime,
  max?: Temporal.PlainTime,
  suggestions: TimeSuggestion[] = [],
): { options: TimeOption[]; suggestions: TimeOption[] } {
  const options: TimeOption[] = []
  const step = granularity === 'hour' ? 60 : 15

  const takenSlots = suggestions?.filter((slot) => slot.isDisabled)
  const availableSlots: TimeOption[] = suggestions
    ?.filter((slot) => {
      if (min && Temporal.PlainTime.compare(slot.time, min) < 0) return false
      if (max && Temporal.PlainTime.compare(slot.time, max) > 0) return false

      return true
    })
    .map((slot) => ({
      ...slot,
      label: toTimeLabel(slot.time, hourCycle),
    }))

  // Generate regular time slots
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += step) {
      if (granularity === 'hour' && m !== 0) continue

      const time = new Temporal.PlainTime(h, m)

      const isSlotTaken = takenSlots?.some(
        (slot) => Temporal.PlainTime.compare(slot.time, time) === 0,
      )

      options.push({
        time,
        label: toTimeLabel(time, hourCycle),
        isDisabled: isSlotTaken,
      })
    }
  }

  // Apply min/max filtering
  const filteredOptions = options.filter((opt) => {
    if (min && Temporal.PlainTime.compare(opt.time, min) < 0) return false
    if (max && Temporal.PlainTime.compare(opt.time, max) > 0) return false

    return true
  })

  // Sort by time (hour, then minute)
  filteredOptions.sort((a, b) => Temporal.PlainTime.compare(a.time, b.time))

  return { options: filteredOptions, suggestions: availableSlots }
}

export const TimeSuggestions = ({
  suggestions,
  handleTimeChange,
  currentValue,
  loading,
  label,
}: {
  suggestions: TimeOption[]
  handleTimeChange: (time: Temporal.PlainTime) => void
  currentValue?: Temporal.PlainTime
  loading?: boolean
  label?: string
}) => {
  const scrollAreaRef = React.useRef<HTMLDivElement>(null)
  const buttonRefs = React.useRef<Array<HTMLButtonElement | null>>([])

  const selectedSuggestionIndex = currentValue
    ? suggestions.findIndex(
        (suggestion) =>
          suggestion.time.hour === currentValue.hour &&
          suggestion.time.minute === currentValue.minute,
      )
    : -1

  const selectedButton = buttonRefs.current[selectedSuggestionIndex]

  React.useEffect((): void => {
    // Scroll to the selected suggestion horizontally
    if (
      selectedSuggestionIndex !== -1 &&
      selectedSuggestionIndex < buttonRefs.current.length &&
      selectedButton
    ) {
      const scrollArea = scrollAreaRef.current

      if (selectedButton && scrollArea) {
        // Calculate the position to center the selected button in the scroll area
        const buttonCenter =
          selectedButton.offsetLeft + selectedButton.offsetWidth / 2
        const scrollAreaCenter = scrollArea.offsetWidth / 2
        const scrollLeft = buttonCenter - scrollAreaCenter

        scrollArea.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: 'smooth',
        })
      }
    }
  }, [selectedButton, selectedSuggestionIndex])

  if (!loading && suggestions.length === 0) return null

  const items = loading ? (
    <div className={styles.suggestionsWrapper}>
      <Skeleton
        show={true}
        className={clsx(styles.suggestionsItem, styles.suggestionsItemSkeleton)}
      />
      <Skeleton
        show={true}
        className={clsx(styles.suggestionsItem, styles.suggestionsItemSkeleton)}
      />
      <Skeleton
        show={true}
        className={clsx(styles.suggestionsItem, styles.suggestionsItemSkeleton)}
      />
    </div>
  ) : (
    suggestions.map((suggestion, index) => (
      <ConditionalTooltip
        align="center"
        className={styles.suggestionsItemWrapper}
        showTooltip={!!suggestion.isDisabled}
        key={`${index}-${suggestion.time.toString()}`}
      >
        <UnstyledButton
          ref={(el) => {
            buttonRefs.current[index] = el
          }}
          className={clsx({
            [styles.suggestionsItem]: true,
            [styles.selected]: selectedSuggestionIndex === index,
            [styles.disabled]: suggestion.isDisabled,
          })}
          onClick={() => handleTimeChange(suggestion.time)}
          disabled={suggestion.isDisabled}
        >
          {suggestion.label}
        </UnstyledButton>
      </ConditionalTooltip>
    ))
  )

  return (
    <>
      <Label size="small" className={styles.timeFieldLabel}>
        {label || 'Suggested Times'}
      </Label>
      <ScrollArea
        size="small"
        gradientFade={40}
        ref={scrollAreaRef}
        scrollbars="horizontal"
        visibility="none"
      >
        <div className={styles.suggestionsScrollAreaInner}>{items}</div>
      </ScrollArea>
    </>
  )
}

export interface TimeInputProps {
  /**
   * The current value (controlled)
   * Uses the Temporal API PlainTime object from @js-temporal/polyfill
   */
  value?: Temporal.PlainTime
  /**
   * The default value (uncontrolled)
   * Uses the Temporal API PlainTime object from @js-temporal/polyfill
   */
  defaultValue?: Temporal.PlainTime
  /** Handler that is called when the value changes */
  onChange?: (value: Temporal.PlainTime | null) => void
  /** Whether the time field is disabled */
  disabled?: boolean
  /** Whether the time field is read only */
  readOnly?: boolean
  /** Whether the time field is required */
  required?: boolean
  /** Whether to use 12 or 24 hour format
   * @default Based on user's locale
   */
  hourCycle?: 12 | 24
  /** The granularity of the time field
   * @default 'minute'
   */
  granularity?: 'hour' | 'minute' | 'second'
  /** The minimum allowed time */
  min?: Temporal.PlainTime
  /** The maximum allowed time */
  max?: Temporal.PlainTime
  /** The placeholder value */
  placeholder?: Temporal.PlainTime
  /** The timezone for the time field */
  timezone?: string
  /** Additional CSS class for the time field */
  className?: string
  /** Size of the input
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large'
  /**
   * Element id for accessibility association with label
   */
  id?: string
  /**
   * Time suggestions configuration object
   */
  timeSuggestions?: {
    data: TimeSuggestion[]
    loading: boolean
    label: string
  }
}

/**
 * Time field component that allows users to enter and edit time values using a keyboard.
 * Each part of a time value (hours, minutes, seconds) is displayed in an individually editable segment.
 */
export const TimeInput = React.forwardRef<HTMLDivElement, TimeInputProps>(
  (
    {
      value,
      defaultValue,
      onChange,
      disabled,
      readOnly,
      required,
      hourCycle = 12, // Default to 12-hour format to match the design
      granularity = 'minute',
      min,
      max,
      placeholder,
      className,
      size = 'medium',
      id,
      timeSuggestions,
      timezone,
      ...otherProps
    },
    ref,
  ) => {
    // Force en-US locale for consistent AM/PM formatting without periods
    const timeLocale = 'en-US'
    const [dropdownOpen, setDropdownOpen] = React.useState(false)

    // Internal state to handle controlled/uncontrolled component
    const [internalValue, setInternalValue] = React.useState<
      Temporal.PlainTime | undefined
    >(value || defaultValue)

    // Update internal state when props change
    React.useEffect(() => {
      if (value !== undefined) {
        setInternalValue(value)
      }
    }, [value])

    // Effective value (controlled via props or internal state)
    const effectiveValue = value !== undefined ? value : internalValue

    // Convert Temporal API objects to React Aria's expected format
    const timeValue = effectiveValue
      ? plainTimeToTimeValue(effectiveValue)
      : undefined
    const timeDefaultValue = defaultValue
      ? plainTimeToTimeValue(defaultValue)
      : undefined
    const timeMin = min ? plainTimeToTimeValue(min) : undefined
    const timeMax = max ? plainTimeToTimeValue(max) : undefined
    const timePlaceholder = placeholder
      ? plainTimeToTimeValue(placeholder)
      : undefined

    // Create state for time field
    const state = useTimeFieldState({
      value: timeValue,
      defaultValue: timeDefaultValue,
      onChange: (value) => {
        let newTime = value ? timeValueToPlainTime(value) : null
        // Clamp to min/max if defined
        if (newTime) {
          if (min && Temporal.PlainTime.compare(newTime, min) < 0) {
            newTime = min
          }
          if (max && Temporal.PlainTime.compare(newTime, max) > 0) {
            newTime = max
          }
        }
        // Update internal state if uncontrolled
        if (onChange === undefined) {
          setInternalValue(newTime || undefined)
        }
        // Call external onChange if provided
        else if (onChange) {
          onChange(newTime)
        }
      },
      locale: timeLocale,
      isDisabled: disabled,
      isReadOnly: readOnly,
      isRequired: required,
      hourCycle,
      granularity,
      minValue: timeMin,
      maxValue: timeMax,
      placeholderValue: timePlaceholder,
    })

    const innerRef = React.useRef<HTMLDivElement>(null)
    const mergedRef = useMergeRefs(ref, innerRef)

    const { fieldProps } = useTimeField(
      {
        ...otherProps,
        value: timeValue,
        defaultValue: timeDefaultValue,
        onChange: (value) => {
          if (onChange) {
            onChange(value ? timeValueToPlainTime(value) : null)
          }
        },
        granularity,
        minValue: timeMin,
        maxValue: timeMax,
        isDisabled: disabled,
        isReadOnly: readOnly,
        isRequired: required,
        hourCycle,
        id,
      },
      state,
      innerRef,
    )

    const { options: dropdownTimeOptions, suggestions } = React.useMemo(
      () =>
        generateTimeOptions(
          hourCycle,
          granularity,
          min,
          max,
          timeSuggestions?.data,
        ),
      [hourCycle, granularity, min, max, timeSuggestions?.data],
    )

    const isSelected = React.useCallback(
      (time: Temporal.PlainTime): boolean => {
        if (!effectiveValue) return false

        if (granularity === 'hour') {
          return effectiveValue.hour === time.hour
        } else {
          return (
            effectiveValue.hour === time.hour &&
            effectiveValue.minute === time.minute
          )
        }
      },
      [effectiveValue, granularity],
    )

    const handleTimeChange = (time: Temporal.PlainTime): void => {
      let newTime = time
      // Clamp to min/max if defined
      if (min && Temporal.PlainTime.compare(newTime, min) < 0) {
        newTime = min
      }
      if (max && Temporal.PlainTime.compare(newTime, max) > 0) {
        newTime = max
      }

      if (onChange === undefined) {
        setInternalValue(newTime)

        state.setValue(
          (plainTimeToTimeValue(newTime) as unknown as DateValue) ?? null,
        )
      } else if (onChange) {
        onChange(newTime)
      }
      setDropdownOpen(false)
    }

    const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (['ArrowDown', 'ArrowUp'].includes(e.key)) {
        e.stopPropagation()
      }
      if (['Enter', ' '].includes(e.key)) {
        e.preventDefault()
        e.stopPropagation()
        if (!disabled && !readOnly && e.key === ' ') {
          setDropdownOpen((open) => !open)
        }
      }
    }

    const { itemRefs, scrollAreaRef } = useScrollToSelectedDropdownItem(
      dropdownOpen,
      dropdownTimeOptions,
      effectiveValue,
      granularity,
    )

    const handleInteractOutside = (e: HandleInteractOutsideEvent): void => {
      const clickOutsideTrigger =
        e.type === 'dismissableLayer.pointerDownOutside' &&
        !isEventOverlappingRef(e, innerRef.current)
      const focusOutsideTrigger =
        e.type === 'dismissableLayer.focusOutside' &&
        !innerRef.current?.contains(e.target as Node)

      if (clickOutsideTrigger || focusOutsideTrigger) {
        setDropdownOpen(false)
      }
    }

    return (
      <Flex direction="column" gap="2xs" fullWidth>
        <TimeSuggestions
          suggestions={suggestions}
          handleTimeChange={handleTimeChange}
          currentValue={effectiveValue}
          loading={timeSuggestions?.loading}
          label={timeSuggestions?.label}
        />

        <Flex fullWidth direction="row" gap="2xs" justify="between">
          <Label size="small" className={styles.timeFieldLabel}>
            Select Time
          </Label>
          {timezone && (
            <Label size="small" className={styles.timeFieldTimezoneLabel}>
              {`${formatTimeZone(timezone)}`}
            </Label>
          )}
        </Flex>
        <DropdownMenu open={dropdownOpen} modal={false}>
          <DropdownMenu.Trigger
            asChild
            disabled={disabled || readOnly}
            onClick={() => {
              if (!disabled && !readOnly) {
                setDropdownOpen(true)
              }
            }}
          >
            <div
              {...fieldProps}
              ref={mergedRef}
              className={clsx(
                styles.timeField,
                styles[size],
                disabled && styles.disabled,
                className,
              )}
            >
              <PinIcon size="small" strokeWidth={2} />
              <div className={styles.timeSegmentsWrapper}>
                {state.segments.map((segment, i) => (
                  <TimeSegment
                    key={i}
                    segment={segment}
                    state={state}
                    onKeyDown={onInputKeyDown}
                  />
                ))}
              </div>
            </div>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content
            className={styles.dropdown}
            side="top"
            sideOffset={10}
            loop={true}
            onEscapeKeyDown={() => {
              setDropdownOpen(false)
            }}
            onInteractOutside={handleInteractOutside}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore - onOpenAutoFocus exists on the underlying Radix primitive but is not exposed in the TypeScript types
            onOpenAutoFocus={(e: React.FocusEvent<HTMLDivElement>) => {
              // Prevent the dropdown from automatically focusing the first item
              // To keep the focus on the segment that triggered the dropdown
              e.preventDefault()
            }}
          >
            <ScrollArea className={styles.scrollArea} ref={scrollAreaRef}>
              {dropdownTimeOptions.map((option, idx) => {
                const selected = isSelected(option.time)
                return (
                  <ConditionalTooltip
                    showTooltip={!!option.isDisabled}
                    key={`${option.time.hour}:${option.time.minute}-${idx}`}
                    className={styles.itemWrapper}
                  >
                    <DropdownMenu.Item
                      ref={(el) => {
                        if (!itemRefs.current) return
                        itemRefs.current[idx] = el
                      }}
                      onClick={() => handleTimeChange(option.time)}
                      disabled={disabled || readOnly || option.isDisabled}
                      className={clsx(
                        styles.item,
                        selected ? styles.selected : '',
                      )}
                      tabIndex={selected ? 0 : -1}
                    >
                      {option.label}
                    </DropdownMenu.Item>
                  </ConditionalTooltip>
                )
              })}
            </ScrollArea>
          </DropdownMenu.Content>
        </DropdownMenu>
      </Flex>
    )
  },
)

TimeInput.displayName = 'TimeInput'

/**
 * Custom hook to scroll the selected or current time item to the center of the dropdown
 *
 * This hook manages scrolling behavior in the time dropdown to ensure the selected time
 * (or closest time if exact match not found) is visible and centered in the viewport.
 *
 * @param dropdownOpen - Boolean indicating if the dropdown is currently open
 * @param options - Array of time options with hour, minute, label, and optional period
 * @param currentValue - The current time value (if any)
 * @param granularity - The granularity of time selection (hour, minute, second)
 * @returns Object containing refs for the list items and scroll area
 */
function useScrollToSelectedDropdownItem(
  dropdownOpen: boolean,
  options: TimeOption[],
  currentValue?: Temporal.PlainTime,
  granularity: 'hour' | 'minute' | 'second' = 'minute',
): {
  itemRefs: React.RefObject<Array<HTMLDivElement | null>>
  scrollAreaRef: React.RefObject<HTMLDivElement>
} {
  const itemRefs = React.useRef<Array<HTMLDivElement | null>>([])
  const scrollAreaRef = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    if (!dropdownOpen || options.length === 0) return

    let timeoutId: number | NodeJS.Timeout | null = null
    const rafId: number | null = null

    const scrollToItem = (): void => {
      let idx = -1

      if (currentValue) {
        // Find exact match for current value
        idx = options.findIndex((opt) => {
          if (granularity === 'hour') {
            return opt.time.hour === currentValue.hour
          } else {
            return (
              opt.time.hour === currentValue.hour &&
              opt.time.minute === currentValue.minute
            )
          }
        })

        if (idx === -1) {
          // If no exact match, find closest time
          idx = findClosestTimeIndex(
            options,
            currentValue.hour,
            currentValue.minute,
            currentValue.second,
            granularity,
          )
        }
      }

      // Ensure index is valid
      if (idx === -1 || idx >= options.length) {
        idx = 0 // Default to first option if no match found
      }

      const item = itemRefs.current[idx]
      const scrollArea = scrollAreaRef.current
      if (item && scrollArea) {
        const itemCenter = item.offsetTop + item.offsetHeight / 2
        const areaCenter = scrollArea.offsetHeight / 2
        scrollArea.scrollTop = itemCenter - areaCenter
      }
    }

    if (
      typeof window !== 'undefined' &&
      typeof window.requestAnimationFrame === 'function'
    ) {
      window.requestAnimationFrame(scrollToItem)
    } else {
      timeoutId = setTimeout(scrollToItem, 0)
    }

    return (): void => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId)
      }
      if (timeoutId !== null) {
        clearTimeout(timeoutId)
      }
    }
  }, [dropdownOpen, options, currentValue, granularity])

  return { itemRefs, scrollAreaRef }
}

/**
 * Find the index of the closest time option to a given hour, minute and second
 *
 * @param options - Array of time options
 * @param hour - Target hour
 * @param minute - Target minute
 * @param second - Target second (optional)
 * @param granularity - The granularity of time selection
 * @returns Index of the closest time option
 */
function findClosestTimeIndex(
  options: TimeOption[],
  hour: number,
  minute: number,
  second: number = 0,
  granularity: 'hour' | 'minute' | 'second' = 'minute',
): number {
  if (options.length === 0) return -1

  const targetSeconds =
    hour * 60 * 60 +
    (granularity !== 'hour' ? minute * 60 : 0) +
    (granularity === 'second' ? second : 0)

  let closestIndex = 0
  let smallestDiff = Infinity

  options.forEach((option, index) => {
    const optSeconds =
      option.time.hour * 60 * 60 +
      (granularity !== 'hour' ? option.time.minute * 60 : 0) +
      (granularity === 'second' ? option.time.second : 0)
    const diff = Math.abs(targetSeconds - optSeconds)

    if (diff < smallestDiff) {
      smallestDiff = diff
      closestIndex = index
    }
  })

  return closestIndex
}
